<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import {
  NButton,
  NCheckbox,
  NEmpty,
  NFormItem,
  NInput,
  NPopconfirm,
  NTabPane,
  NTabs,
  NTime,
  NTooltip,
  useMessage,
} from 'naive-ui'
import { useRemindersStore, type ReminderInput } from '../stores/reminders'

// The one-time-reminder half of AdminReminders.vue (#1092) — the add/edit
// form, status filter, and list of one-time reminders. Extracted so the
// parent stays under the 300-line hard limit; no behavior change from the
// inline version.

const store = useRemindersStore()
const message = useMessage()

const CONFIRM_TRUNCATE_LENGTH = 50

const reminderMessageRef = ref<InstanceType<typeof NInput> | null>(null)

const reminderForm = reactive<ReminderInput>({ message: '', fire_at: '' })
const editingReminderId = ref<number | null>(null)
const savingReminder = ref(false)

const reminderStatus = ref<'pending' | 'done' | 'all'>('pending')

const REMINDER_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Done', value: 'done' },
  { label: 'All', value: 'all' },
]

async function onReminderStatusChange(status: 'pending' | 'done' | 'all') {
  reminderStatus.value = status
  try {
    await store.fetchReminders(status)
  } catch {
    // loadError handled in the parent's template
  }
}

// Convert a stored UTC ISO string to a datetime-local value in browser local time
function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

async function startEditReminder(id: number) {
  const r = store.reminders.find((reminder) => reminder.id === id)
  if (!r) return
  Object.assign(reminderForm, { message: r.message, fire_at: isoToDatetimeLocal(r.fire_at) })
  editingReminderId.value = id
  await nextTick()
  reminderMessageRef.value?.focus()
}

function cancelEditReminder() {
  editingReminderId.value = null
  Object.assign(reminderForm, { message: '', fire_at: '' })
}

async function saveReminder() {
  if (!reminderForm.message.trim()) {
    message.error('Message is required')
    return
  }
  if (!reminderForm.fire_at) {
    message.error('Date/time is required')
    return
  }
  savingReminder.value = true
  try {
    if (editingReminderId.value !== null) {
      await store.updateReminder(editingReminderId.value, { ...reminderForm })
      message.success('Reminder updated')
    } else {
      await store.createReminder({ ...reminderForm })
      message.success('Reminder created')
    }
    cancelEditReminder()
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save reminder')
  } finally {
    savingReminder.value = false
  }
}

async function toggleDone(id: number, done: boolean) {
  try {
    await store.toggleDone(id, done)
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to update reminder')
  }
}

async function deleteReminder(id: number) {
  try {
    await store.deleteReminder(id)
    message.success('Reminder deleted')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to delete reminder')
  }
}

function isFired(r: { fired_at: string | null }) {
  return r.fired_at !== null
}
</script>

<template>
  <div class="flex flex-col gap-3 mt-3">
    <!-- Add / edit one-time reminder form -->
    <div class="bg-white/5 rounded-lg p-4 flex flex-col gap-3">
      <p class="text-sm font-medium text-white/80 m-0">
        {{ editingReminderId !== null ? 'Edit reminder' : 'Add one-time reminder' }}
      </p>
      <NFormItem label="Message" :show-feedback="false">
        <NInput
          ref="reminderMessageRef"
          v-model:value="reminderForm.message"
          placeholder="e.g. Pick up dry cleaning"
          @keydown.enter.prevent="saveReminder"
        />
      </NFormItem>
      <NFormItem label="Date / time (local)" :show-feedback="false">
        <input
          v-model="reminderForm.fire_at"
          type="datetime-local"
          aria-label="Date and time (local)"
          class="w-full rounded px-3 py-1.5 bg-white/10 text-white text-sm border border-white/20 focus:outline-none focus:border-white/40"
        />
      </NFormItem>
      <div class="flex gap-2">
        <NButton type="primary" :loading="savingReminder" @click="saveReminder">
          {{ editingReminderId !== null ? 'Save changes' : 'Add reminder' }}
        </NButton>
        <NButton v-if="editingReminderId !== null" quaternary @click="cancelEditReminder">Cancel</NButton>
      </div>
    </div>

    <!-- Status filter -->
    <div class="flex items-center gap-2">
      <span class="text-xs text-white/40">Show:</span>
      <NTabs
        :value="reminderStatus"
        type="segment"
        size="small"
        style="--n-tab-padding: 4px 12px"
        @update:value="(v) => onReminderStatusChange(v as 'pending' | 'done' | 'all')"
      >
        <NTabPane v-for="opt in REMINDER_STATUS_OPTIONS" :key="opt.value" :name="opt.value" :tab="opt.label" />
      </NTabs>
    </div>

    <NEmpty
      v-if="store.reminders.length === 0"
      :description="`No ${reminderStatus === 'all' ? '' : reminderStatus + ' '}reminders`"
    />
    <div
      v-for="r in store.reminders"
      :key="r.id"
      class="flex items-start gap-3 bg-white/5 rounded-lg p-3"
      :class="{ 'opacity-50': r.done }"
    >
      <NCheckbox
        :checked="r.done"
        :aria-label="`Mark reminder as done: ${r.message}`"
        @update:checked="(v) => toggleDone(r.id, v)"
      />
      <div class="flex-1 min-w-0">
        <p class="text-sm text-white m-0" :class="{ 'line-through': r.done }">{{ r.message }}</p>
        <p class="text-xs text-white/50 m-0 mt-0.5">
          <NTime :time="new Date(r.fire_at)" />
          <span v-if="isFired(r)" class="ml-2 text-green-400"><span aria-hidden="true">✓</span> sent</span>
          <span v-else class="ml-2 text-yellow-400">pending</span>
          <NTooltip v-if="r.reminder_schedule_id" placement="top">
            <template #trigger>
              <span class="ml-2 text-white/30 cursor-help">from schedule</span>
            </template>
            Generated by a recurring schedule — edit the schedule to change this.
          </NTooltip>
        </p>
      </div>
      <NButton
        v-if="!r.reminder_schedule_id"
        size="tiny"
        quaternary
        :aria-label="`Edit reminder: ${r.message}`"
        @click="startEditReminder(r.id)"
        >Edit</NButton
      >
      <NPopconfirm positive-text="Delete" negative-text="Cancel" @positive-click="deleteReminder(r.id)">
        <template #trigger>
          <NButton size="tiny" quaternary type="error" :aria-label="`Delete reminder: ${r.message}`">Delete</NButton>
        </template>
        Delete reminder "{{
          r.message.length > CONFIRM_TRUNCATE_LENGTH ? r.message.slice(0, CONFIRM_TRUNCATE_LENGTH) + '…' : r.message
        }}"?
      </NPopconfirm>
    </div>
  </div>
</template>
